import React, { useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useDispatch, useSelector } from 'react-redux';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import moment from 'moment';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import {
  mapActivitiesCategoriesWithAdditionnalServices,
  TOAST_DURATION,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import {
  addOrReplaceAdditionalServices,
  deleteAdditionalServices,
} from 'Store/booking/proposals/proposals.action';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import {
  TRACK_BE_EVENT_GA_ACTIVITIES,
  TRACK_CLICK_EVENT_BE_RENTALS,
} from 'Tracking/events/be.tracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { trackErrorOnAddService } from 'Views/booking/BookingEngine.utils';
import { Title } from 'Components/frameworks/title/Title';
import { ActivitiesCategories } from 'Components/widgets/activities-categories/ActivitiesCategories';
import { ActivitiesThematics } from 'Components/widgets/activities-thematics/ActivitiesThematics';
import { RentalsCta } from 'Components/widgets/rentals-cta/RentalsCta';
import { SkiPackageReassurance } from 'Components/widgets/ski-package-reassurance/SkiPackageReassurance';
import { ActivitiesBooking } from './ActivitiesBooking';
type Props = {
  categories: Array<ActivitiesCategoryEntity>;
  hasRentals: boolean;
  proposals: ReadonlyArray<Proposal>;
  resort: Product;
  selectedProposal: Proposal;
  thematics: Array<ActivitiesThematicEntity>;
};
export const ProposalActivitiesBooking = ({
  categories,
  hasRentals,
  proposals,
  resort,
  selectedProposal,
  thematics,
  ...rest
}: Props) => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const { isDesktop } = useResponsive();
  const { trackClickEvent } = useTrackEvent();
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const { attendees: attendeesWithInfos } = useSelector(selectAttendeesWithInfosByHouseholds);
  const track = useTracking(TRACK_BE_EVENT_GA_ACTIVITIES);
  useEffect(() => {
    let timer;

    if (updateInfo) {
      timer = setTimeout(() => setUpdateInfo(null), TOAST_DURATION);
    }

    return () => clearTimeout(timer);
  }, [updateInfo]);
  const activitiesCategoriesWithAdditionnalServices =
    mapActivitiesCategoriesWithAdditionnalServices(
      categories,
      selectedProposal.additionalServices.activities,
      {
        filtered: false,
      },
    );
  const activitiesThematicsWithAdditionnalServices = mapActivitiesCategoriesWithAdditionnalServices(
    thematics,
    selectedProposal.additionalServices.activities,
    {
      filtered: false,
    },
  );
  return (
    <ActivitiesBooking
      {...rest}
      categories={activitiesCategoriesWithAdditionnalServices}
      contextValue={{
        attendeesList: [
          ...attendeesWithInfos.adults,
          ...attendeesWithInfos.children.map((child) => ({ ...child, isChild: true })),
        ],
        availableActivities: selectedProposal.additionalServices.activities,
        currency: selectedProposal.price.currency,
        dates: {
          arrivalDate: moment(selectedProposal.arrivalDate, API_DATE_FORMAT).valueOf(),
          departureDate: moment(selectedProposal.departureDate, API_DATE_FORMAT).valueOf(),
        },
        isPriceForAdultOnly: true,
        hasRentals,
        scheduleReservationMessage: t('booking.activities.sportActivities.changeSchedule'),
        setUpdateInfo,
        trackingData: TRACK_BE_EVENT_GA_ACTIVITIES,
        updateInfo,
        updateService: (
          attendees: ServiceMutationType,
          pricingInfo: PricingInfoWithScheduleType,
          serviceIds: ReadonlyArray<string>,
          attendeeIds: ReadonlyArray<string>,
        ) => {
          const info = {
            itemsNumber: attendees.length,
            message: attendees.length
              ? t('booking.activities.newActivityAdded')
              : t('booking.activities.newActivityRemoved'),
          };

          if (attendees.length === 0) {
            return dispatch(
              deleteAdditionalServices({
                appContext,
                attendeeIds,
                serviceIds,
                showNotification: true,
              }),
            ).then(() => setUpdateInfo(info));
          }

          return dispatch(
            addOrReplaceAdditionalServices({
              appContext,
              attendees,
              showNotification: true,
            }),
          )
            .then(() => setUpdateInfo(info))
            .catch((error) => trackErrorOnAddService(error, track, 'RENTAL_NOT_FOUND'));
        },
        showRentAllSchedules: true,
      }}
      thematics={activitiesThematicsWithAdditionnalServices}
    >
      <div className="justify-between md:flex">
        {resort.isWinter && !resort.isTour && !resort.isCruise && (
          <aside className="order-1 mb-20 md:mb-0 md:w-1/4" data-testid="Activities-asideText">
            <SkiPackageReassurance hasRentals={hasRentals} />
          </aside>
        )}
        <div
          className={classnames('flex flex-col gap-20', {
            'md:w-2/3': resort.isWinter,
          })}
          data-testid="activities-rentals"
        >
          {hasRentals && (
            <RentalsCta className="h-310" onClick={() => track(TRACK_CLICK_EVENT_BE_RENTALS)} />
          )}
          <ActivitiesCategories
            {...rest}
            categories={activitiesCategoriesWithAdditionnalServices}
            colsNumber={resort.isWinter && isDesktop ? 4 : 6}
            hasCounters
            hasOnlineBookableCount
            onClickCard={(type, hasOnlineBooking) => {
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACTIVITIES, {
                detail_click: 'cards',
                availability: hasOnlineBooking ? 'yes' : 'no',
                activity_type: type?.toLowerCase(),
              });
            }}
          />
        </div>
      </div>
      {activitiesThematicsWithAdditionnalServices.length > 0 && (
        <div className="mt-40">
          <p className="mb-10 text-16 text-mediumGrey" data-testid="Activites-title">
            {t('common.activities.findYourThematic')}
          </p>
          <Title className="mb-20" theme="title-brand-n3">
            {t('common.activities.thematicTitle')}
          </Title>
          <ActivitiesThematics
            {...rest}
            hasCounters
            isProposal
            thematics={activitiesThematicsWithAdditionnalServices}
          />
        </div>
      )}
    </ActivitiesBooking>
  );
};
